import { useLocation } from '@/lib/router-compat';
import { useLanguage } from '@/context/LanguageContext';
import type { Product } from '@/data/products';
import { canonicalUrl } from '@/seo/internationalRoutes';
import { merchantOffer, uniqueProductImages, type StorefrontVariant } from '@/seo/merchantProduct';

type Props = {
  product: Product;
  name: string;
  description: string;
  variant: StorefrontVariant | null | undefined;
  visibleImages: string[];
};

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export default function AccessoryProductJsonLd({
  product, name, description, variant, visibleImages,
}: Props) {
  const { pathname } = useLocation();
  const { t, localePath } = useLanguage();
  const productUrl = canonicalUrl('nl', pathname);
  const categoryUrl = canonicalUrl('nl', localePath('/accessoires'));
  const offer = merchantOffer(productUrl, variant);
  const images = uniqueProductImages(visibleImages);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        '@id': `${productUrl}#product`,
        url: productUrl,
        name,
        description: stripHtml(description),
        image: images,
        sku: product.id,
        brand: { '@type': 'Brand', name: 'Rave Safe' },
        itemCondition: 'https://schema.org/NewCondition',
        ...(offer ? { offers: offer } : {}),
        // Store reviews from WebwinkelKeur are not individual product reviews.
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${productUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t.common.home, item: 'https://ravesafe.nl/' },
          { '@type': 'ListItem', position: 2, name: t.acc.title, item: categoryUrl },
          { '@type': 'ListItem', position: 3, name, item: productUrl },
        ],
      },
      {
        '@type': 'Organization',
        '@id': 'https://ravesafe.nl/#organization',
        name: 'Rave Safe',
        legalName: 'Rave Safe B.V.',
        url: 'https://ravesafe.nl/',
      },
    ],
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}
